/**
 * Where the picture actually is inside a video element. A <video> with
 * object-fit: contain letterboxes or pillarboxes its picture, so anything that
 * must sit on the picture (subtitles) is positioned against this rectangle, not
 * against the element or the stage around it.
 */
import { useEffect, useState } from 'react';

export interface ContentRect { left: number; top: number; width: number; height: number }

/** The object-fit: contain rectangle of a media of mediaW x mediaH inside a box of boxW x boxH (null when a size is unknown). */
export function containRect(boxW: number, boxH: number, mediaW: number, mediaH: number): ContentRect | null {
  if (!(boxW > 0) || !(boxH > 0) || !(mediaW > 0) || !(mediaH > 0)) return null;
  const scale = Math.min(boxW / mediaW, boxH / mediaH);
  const width = mediaW * scale;
  const height = mediaH * scale;
  return { left: (boxW - width) / 2, top: (boxH - height) / 2, width, height };
}

/*
 * 1.9.0: subtitle placement left this file. Every renderer lays cues out with contracts cueLayout in the picture
 * rectangle below (subtitles/CueOverlay.tsx cueStyles), as the export burn-in does.
 */

/**
 * The picture rectangle, in the coordinates of the element's offset parent
 * (the stage the overlay is absolutely positioned in), kept current with a
 * ResizeObserver. `media` overrides the intrinsic size (a sequence viewer
 * knows its frame size); otherwise it is the video's videoWidth/videoHeight.
 */
export function useVideoContentRect(el: HTMLElement | null, media?: { width: number; height: number }): ContentRect | null {
  const [rect, setRect] = useState<ContentRect | null>(null);
  const mw = media?.width;
  const mh = media?.height;
  useEffect(() => {
    if (!el) { setRect(null); return; }
    const measure = () => {
      const w = mw ?? (el as HTMLVideoElement).videoWidth;
      const h = mh ?? (el as HTMLVideoElement).videoHeight;
      const r = containRect(el.clientWidth, el.clientHeight, w, h);
      // A video sits at some offset inside its stage; a stage measured directly is its own origin.
      const dx = media ? 0 : el.offsetLeft;
      const dy = media ? 0 : el.offsetTop;
      setRect((prev) => {
        if (!r) return prev === null ? prev : null;
        const next = { left: r.left + dx, top: r.top + dy, width: r.width, height: r.height };
        return prev && prev.left === next.left && prev.top === next.top && prev.width === next.width && prev.height === next.height ? prev : next;
      });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    el.addEventListener('loadedmetadata', measure);
    el.addEventListener('resize', measure);
    return () => { ro?.disconnect(); el.removeEventListener('loadedmetadata', measure); el.removeEventListener('resize', measure); };
  }, [el, mw, mh, media === undefined]);
  return rect;
}

/** An element's inner size (clientWidth × clientHeight), kept current with a ResizeObserver (1.7.0: Side by side's area). */
export function useElementSize(el: HTMLElement | null): { width: number; height: number } | null {
  const [size, setSize] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    if (!el) { setSize(null); return; }
    const measure = () => setSize((prev) => (prev && prev.width === el.clientWidth && prev.height === el.clientHeight ? prev : { width: el.clientWidth, height: el.clientHeight }));
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [el]);
  return size;
}

/**
 * The largest box of `media`'s shape inside an element (1.0.0-rc.11), kept current with a ResizeObserver: the
 * sequence viewer's stage keeps the sequence's aspect (a 9:16 sequence is a tall box in a wide pane), so a Fill clip
 * covers exactly the sequence frame.
 */
export function useFittedBox(el: HTMLElement | null, media: { width: number; height: number }): { width: number; height: number } | null {
  const [box, setBox] = useState<{ width: number; height: number } | null>(null);
  useEffect(() => {
    if (!el) { setBox(null); return; }
    const measure = () => {
      const r = containRect(el.clientWidth, el.clientHeight, media.width, media.height);
      setBox((prev) => {
        if (!r) return prev === null ? prev : null;
        const next = { width: Math.floor(r.width), height: Math.floor(r.height) };
        return prev && prev.width === next.width && prev.height === next.height ? prev : next;
      });
    };
    measure();
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    return () => ro?.disconnect();
  }, [el, media.width, media.height]);
  return box;
}
